"use client";

import React, { FC, memo } from "react";
import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";
import { trackFailureScenarioRetryClick } from "../../../components/News/components/trackFailureScenarioRetry";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import {
  campaignName,
  journeyTag,
  productCategory,
  referenceId,
  segmentName,
  sprint,
} from "../../News/newsTrackingConstants";

export interface StockListingErrorProps {
  /** Error title */
  errorTitle?: string;
  /** Error description */
  errorDescription?: string;
  /** Retry button text */
  errorRetryButtonText?: string;
  /** Error image source */
  errorImageSrc?: string;
  /** Error image alt text */
  errorImageAlt?: string;
  /** Retry callback function */
  onRetry?: () => void;
}

/**
 * Client component for error state in stock listing pages.
 * Handles error display with retry functionality, following the same pattern as TrendingNewsError.
 */
const StockListingError: FC<StockListingErrorProps> = memo(
  ({
    errorTitle = "Unable to fetch stock information",
    errorDescription = "Something went wrong, please wait a moment, then try again",
    errorRetryButtonText = "Retry",
    errorImageSrc = "",
    errorImageAlt = "Error",
    onRetry,
  }) => {
    const handleRetry = () => {
      if (onRetry) {
        onRetry();
      } else if (typeof window !== "undefined") {
        window.location.reload();
      }
    };

    return (
      <div className="flex items-center justify-center py-12 min-h-[400px]">
        <BFLApiFailure
          imageSrc={errorImageSrc}
          imageAlt={errorImageAlt}
          error={errorTitle}
          errorDescription={errorDescription}
          retryButtonText={errorRetryButtonText}
          onRetry={handleRetry}
          trackingConfig={{
            sectionTitle: `${errorTitle} (Stock Listing)`,
            componentPosition: "1",
            entryPoint: "no_result_found",
            productCategory,
            subSection: "Stock Listing",
            segmentName,
            journeyTag,
            campaignName,
            sprint,
            referenceId,
          }}
          onRetryClick={(params) =>
            trackFailureScenarioRetryClick(params, getCustomerIdForAnalytics)
          }
        />
      </div>
    );
  },
);

StockListingError.displayName = "StockListingError";

export default StockListingError;

